import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, Clock, Sparkles, Leaf } from "lucide-react";
import BotanicalBackdrop, { LeafGlyph } from "@/components/BotanicalBackdrop";
import BotanicalIcon from "@/components/BotanicalIcon";
import ProductImage from "@/components/ProductImage";
import { IMAGES } from "@/data/products";
import { useProducts } from "@/context/ProductsContext";
import { INGREDIENTS } from "@/data/ingredients";
import { useEnquiry } from "@/App";
import { HKMonogram } from "@/components/BrandMark";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
import { useContent } from "@/context/PageContentContext";
import usePageTitle from "@/hooks/usePageTitle";

// ------- Auto-playing product slider (fade cross-dissolve) -------
const AUTOPLAY_MS = 4500;

const ProductSlider = () => {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const enq = useEnquiry();
  const { allProducts } = useProducts();
  const touchStart = useRef(null); // { x, y, t }
  const touchMoved = useRef(false);
  const timerRef = useRef(null);
  const total = allProducts.length;

  const go = useCallback((i) => {
    setActive(((i % total) + total) % total);
  }, [total]);

  const next = useCallback(() => go(active + 1), [active, go]);
  const prev = useCallback(() => go(active - 1), [active, go]);

  useEffect(() => {
    if (paused) return;
    timerRef.current = setTimeout(next, AUTOPLAY_MS);
    return () => clearTimeout(timerRef.current);
  }, [active, paused, next]);

  const onTouchStart = (e) => {
    const t = e.touches[0];
    touchStart.current = { x: t.clientX, y: t.clientY, t: Date.now() };
    touchMoved.current = false;
  };
  const onTouchMove = (e) => {
    if (!touchStart.current) return;
    const t = e.touches[0];
    const dx = Math.abs(t.clientX - touchStart.current.x);
    const dy = Math.abs(t.clientY - touchStart.current.y);
    if (dx > 10 || dy > 10) touchMoved.current = true;
  };
  const onTouchEnd = (e) => {
    if (!touchStart.current) return;
    const dx = e.changedTouches[0].clientX - touchStart.current.x;
    if (Math.abs(dx) > 40) {
      dx < 0 ? next() : prev();
    }
    touchStart.current = null;
    setPaused(true);
    setTimeout(() => setPaused(false), 800);
  };

  // Prevent a tap-through nav if the user was swiping
  const handleSlideClickCapture = (e) => {
    if (touchMoved.current) {
      e.preventDefault();
      e.stopPropagation();
      touchMoved.current = false;
    }
  };

  const handleManual = (fn) => (e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    fn();
    setPaused(true);
    setTimeout(() => setPaused(false), 1200);
  };

  return (
    <div
      className="relative"
      data-testid="home-product-slider"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      {/* Soft warm glow */}
      <div className="absolute -inset-6 rounded-[24px] blur-2xl opacity-60 pointer-events-none"
        style={{ background: "radial-gradient(closest-side, rgba(217,117,53,0.22), rgba(201,169,97,0.14), transparent 70%)" }} />

      <div className="relative aspect-[4/5] rounded-[20px] overflow-hidden bg-white border border-[var(--hk-border)] shadow-[0_30px_80px_-30px_rgba(30,35,31,0.35)] transition-shadow duration-500 hover:shadow-[0_40px_100px_-30px_rgba(217,117,53,0.35)]">
        {allProducts.map((p, i) => {
          const isActive = i === active;
          return (
            <Link
              key={p.slug}
              to={`/products/${p.slug}`}
              aria-hidden={!isActive}
              tabIndex={isActive ? 0 : -1}
              onClickCapture={handleSlideClickCapture}
              className={
                "absolute inset-0 block cursor-pointer transition-[opacity,transform] duration-[900ms] ease-out group " +
                (isActive
                  ? "opacity-100"
                  : "opacity-0 pointer-events-none")
              }
              data-testid={`slide-${p.slug}`}
            >
              {/* Product-specific soft background */}
              <div className="absolute inset-0" style={{
                background:
                  p.slug === "sea-buckthorn-pure-pulp"
                    ? "linear-gradient(160deg, #FDF7EE 0%, #F3E4CC 100%)"
                    : p.slug === "man-360"
                    ? "linear-gradient(160deg, #F5EFE4 0%, #E4D6BC 100%)"
                    : p.slug === "shilajit-for-men"
                    ? "linear-gradient(160deg, #EFE7DA 0%, #D9C8AA 100%)"
                    : p.slug === "shilajit-for-women"
                    ? "linear-gradient(160deg, #F5EBDD 0%, #E8CDBE 100%)"
                    : "linear-gradient(160deg, #F0F0E4 0%, #D9DAC1 100%)"
              }} />

              {/* Botanical corner watermark */}
              <div className="absolute -top-6 -right-6 w-40 h-40 opacity-[0.09] text-[var(--hk-orange-deep)]" aria-hidden="true">
                <BotanicalIcon variant={p.slug === "man-360" ? "root" : p.slug.includes("shilajit") ? "mineral" : "berry"} />
              </div>

              {/* Product visual */}
              <div className="absolute inset-0 flex items-center justify-center p-8 md:p-14">
                <ProductImage product={p} className="w-full h-full" imgClassName="object-contain" />
              </div>

              {/* Status pill (top-left) */}
              <div className="absolute top-5 left-5 flex flex-wrap gap-2">
                {p.status === "available" ? (
                  <>
                    <span className="px-3 py-1 rounded-full bg-white/95 backdrop-blur border border-[var(--hk-border)] text-[10px] tracking-[0.2em] uppercase text-[var(--hk-ink)]" data-testid="slide-status-available">
                      Available Now
                    </span>
                    {p.offer && (
                      <span className="px-3 py-1 rounded-full bg-[var(--hk-orange)] text-white text-[10px] tracking-[0.2em] uppercase shadow-[0_8px_18px_rgba(217,117,53,0.32)]">
                        {p.offer}
                      </span>
                    )}
                  </>
                ) : (
                  <span className="px-3 py-1 rounded-full bg-[var(--hk-ink)] text-white text-[10px] tracking-[0.2em] uppercase inline-flex items-center gap-1">
                    <Clock size={10} /> {p.statusLabel}
                  </span>
                )}
              </div>

              {/* Info block (bottom) */}
              <div className="absolute inset-x-0 bottom-0 p-6 md:p-8 bg-gradient-to-t from-white/95 via-white/75 to-transparent backdrop-blur-[2px]">
                <div className="label-eyebrow">{p.packSize}</div>
                <h3 className="serif mt-1.5 text-[22px] md:text-[26px] leading-tight text-[var(--hk-ink)]" data-testid={`slide-name-${p.slug}`}>{p.name}</h3>
                <p className="mt-1.5 text-[13px] leading-relaxed text-[var(--hk-ink-2)] max-w-md">{p.shortLine}</p>
                <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
                  {p.status === "available" ? (
                    <span className="serif text-[22px] text-[var(--hk-ink)]" data-testid={`slide-price-${p.slug}`}>{p.priceDisplay}</span>
                  ) : (
                    <span className="label-eyebrow !text-[var(--hk-orange-deep)]">Coming Soon</span>
                  )}
                  {p.status === "available" ? (
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="hk-btn-primary !py-2 !px-4 !text-[12px]" data-testid={`slide-buy-${p.slug}`}>
                        Buy Now <ArrowRight size={13} />
                      </span>
                      <span className="hk-btn-ghost !py-2 !px-4 !text-[12px]" data-testid={`slide-view-${p.slug}`}>
                        View Product
                      </span>
                    </div>
                  ) : (
                    <span className="hk-btn-primary !py-2 !px-4 !text-[12px]" data-testid={`slide-view-${p.slug}`}>
                      View Product <ArrowRight size={13} />
                    </span>
                  )}
                </div>
              </div>

              {/* Subtle hover glow — makes clickability obvious on desktop */}
              <div
                className="absolute inset-0 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                style={{ boxShadow: "inset 0 0 0 2px rgba(217,117,53,0.35)" }}
                aria-hidden="true"
              />
            </Link>
          );
        })}

        {/* Arrows */}
        <button
          onClick={handleManual(prev)}
          className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/95 backdrop-blur border border-[var(--hk-border)] flex items-center justify-center text-[var(--hk-ink)] hover:text-[var(--hk-orange-deep)] shadow-[0_10px_20px_rgba(30,35,31,0.12)] transition-colors"
          data-testid="slider-prev"
          aria-label="Previous product"
        >
          <ArrowLeft size={16} />
        </button>
        <button
          onClick={handleManual(next)}
          className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/95 backdrop-blur border border-[var(--hk-border)] flex items-center justify-center text-[var(--hk-ink)] hover:text-[var(--hk-orange-deep)] shadow-[0_10px_20px_rgba(30,35,31,0.12)] transition-colors"
          data-testid="slider-next"
          aria-label="Next product"
        >
          <ArrowRight size={16} />
        </button>
      </div>

      {/* Dots */}
      <div className="mt-5 flex items-center justify-center gap-2" data-testid="slider-dots">
        {allProducts.map((p, i) => (
          <button
            key={p.slug}
            onClick={handleManual(() => go(i))}
            aria-label={`Show ${p.name}`}
            data-testid={`slider-dot-${i}`}
            className={"h-1.5 rounded-full transition-all duration-300 " + (i === active ? "w-8 bg-[var(--hk-orange)]" : "w-3 bg-[var(--hk-border)] hover:bg-[var(--hk-gold)]")}
          />
        ))}
      </div>
      {/* Screen-reader current-slide label */}
      <div className="mt-3 text-[11px] tracking-[0.24em] uppercase text-[var(--hk-ink-3)] text-center">
        <span data-testid="slider-active-label">{allProducts[active]?.name}</span>
        <span className="mx-2 opacity-40">·</span>
        <span>{active + 1} / {total}</span>
      </div>
    </div>
  );
};

// ------- Botanical grid for section 6 -------
const BOTANICAL_GRID = INGREDIENTS.slice(0, 14).map((ing) => ing); // 13 herbs
const SEA_BUCK_ADD = {
  id: "sea-buckthorn",
  name: "Sea Buckthorn",
  latin: "Hippophae rhamnoides",
  category: "Himalayan Berry",
  iconVariant: "berry",
  accent: "#D97535",
};

const HomePage = () => {
  usePageTitle(null); // root uses full brand title
  const enq = useEnquiry();

  // CMS-driven hero copy (falls back to hardcoded defaults)
  const heroEyebrow = useContent("home", "hero.eyebrow", "A Brand Rooted in Tradition");
  const heroLine1   = useContent("home", "hero.title.line1", "Ayurveda for");
  const heroLine2   = useContent("home", "hero.title.line2", "modern living.");
  const heroCTA1    = useContent("home", "hero.cta.primary", "Explore Our Collection");
  const heroCTA2    = useContent("home", "hero.cta.secondary", "Read Our Story");

  const botanicalRow = useMemo(() => [SEA_BUCK_ADD, ...BOTANICAL_GRID], []);

  // Admin-managed botanicals — when present, override the hardcoded grid entirely
  const [adminBotanicals, setAdminBotanicals] = useState(null);
  useEffect(() => {
    axios.get(`${API}/botanicals?home_only=true`).then((r) => setAdminBotanicals(r.data || [])).catch(() => setAdminBotanicals([]));
  }, []);

  const finalBotanicalRow = (adminBotanicals && adminBotanicals.length > 0)
    ? adminBotanicals.map((b, i) => ({
        id: b.id || `b-${i}`,
        name: b.name_en,
        latin: b.scientific,
        category: b.plant_part || (b.name_hi || ""),
        iconVariant: (b.name_en || "").toLowerCase().includes("shilajit") ? "mineral"
          : (b.name_en || "").toLowerCase().includes("berry") || (b.plant_part || "").toLowerCase().includes("berry") ? "berry"
          : "root",
        accent: ["#D97535", "#B85E24", "#7B8F5C", "#4A6B4A", "#8A6E1F"][i % 5],
        image: b.image,
      }))
    : botanicalRow;

  return (
    <>
      {/* ================= SECTION 1 — Hero ================= */}
      <section className="relative pt-14 pb-20 lg:pt-20 lg:pb-24 overflow-hidden" data-testid="home-hero">
        <BotanicalBackdrop variant="corners" />
        <div className="mx-auto max-w-7xl px-6 lg:px-10 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          {/* LEFT */}
          <div className="lg:col-span-6 hk-fade-up">
            {/* Brand lockup — vector monogram + wordmark */}
            <div className="flex items-center gap-4" data-testid="home-hero-brand-lockup">
              <HKMonogram size={72} withRoundel={true} />
              <div className="leading-[1.15]">
                <div
                  className="serif text-[28px] md:text-[34px] font-medium tracking-[0.02em] text-[var(--hk-ink)]"
                  data-testid="home-hero-wordmark"
                  style={{ paddingBottom: "0.06em" }}
                >
                  HerbKranti
                </div>
                <div
                  className="mt-[6px]"
                  style={{
                    fontFamily: "'Manrope', sans-serif",
                    fontSize: "10px",
                    letterSpacing: "0.32em",
                    color: "var(--hk-gold-deep)",
                    fontWeight: 500,
                    textTransform: "uppercase",
                  }}
                >
                  Rediscover Nature · Redefine Wellness
                </div>
              </div>
            </div>

            <div className="mt-10 flex items-center gap-3">
              <span className="label-eyebrow inline-flex items-center gap-1.5">
                <LeafGlyph className="w-3 h-3 text-[var(--hk-gold-deep)]" />
                {heroEyebrow}
              </span>
            </div>

            <h1 className="serif mt-4 text-[42px] sm:text-[54px] lg:text-[68px] leading-[1.1] tracking-tight text-[var(--hk-ink)]" data-testid="home-hero-title" style={{ paddingBottom: "0.05em" }}>
              {heroLine1}
              <br />
              <span className="italic-accent text-[var(--hk-orange-deep)]">{heroLine2}</span>
            </h1>
            <p className="mt-4 serif text-[20px] sm:text-[22px] italic text-[var(--hk-ink-2)] leading-snug" data-testid="home-hero-subtitle">
              Ancient Wisdom. Thoughtfully Reimagined by HerbKranti.
            </p>

            <p className="mt-6 text-[15.5px] leading-[1.85] text-[var(--hk-ink-2)] max-w-lg">
              Ayurveda is more than a set of remedies. It is one of the world's oldest philosophies of everyday living — a considered way of understanding balance, nature, nourishment, rest and the quiet rituals that shape long-term well-being.
            </p>
            <p className="mt-4 text-[15.5px] leading-[1.85] text-[var(--hk-ink-2)] max-w-lg">
              HerbKranti brings that traditional botanical wisdom into modern life through thoughtfully developed wellness products, premium presentation and honest communication — not miracle claims, not marketing shortcuts. Just considered products, carefully made.
            </p>

            <div className="mt-9 flex flex-wrap items-center gap-4">
              <Link to="/products" className="hk-btn-primary" data-testid="home-hero-explore-btn">
                {heroCTA1} <ArrowRight size={16} />
              </Link>
              <Link to="/our-story" className="hk-btn-ghost" data-testid="home-hero-products-btn">
                {heroCTA2}
              </Link>
            </div>
          </div>

          {/* RIGHT — auto-playing slider */}
          <div className="lg:col-span-6">
            <ProductSlider />
          </div>
        </div>
      </section>

      {/* ================= SECTION 2 — What Ayurveda Really Means ================= */}
      <section className="relative py-24 lg:py-28 bg-[var(--hk-warm-ivory)] overflow-hidden">
        <BotanicalBackdrop variant="sideRight" />
        <div className="mx-auto max-w-4xl px-6 lg:px-10 text-center">
          <div className="label-eyebrow">The Philosophy</div>
          <h2 className="serif mt-4 text-[36px] sm:text-[46px] lg:text-[54px] leading-[1.05] text-[var(--hk-ink)]" data-testid="section-ayurveda-title">
            Ayurveda is a way of{" "}
            <span className="italic-accent text-[var(--hk-orange-deep)]">living in balance.</span>
          </h2>
          <div className="mt-8 space-y-6 text-[16px] leading-[1.95] text-[var(--hk-ink-2)] text-left md:text-center max-w-3xl mx-auto">
            <p>
              At its heart, Ayurveda is a traditional Indian approach to everyday well-being — a philosophy that recognises that how we live, eat, rest, move and relate to the natural world quietly shapes how we feel over time.
            </p>
            <p>
              It teaches us to live in harmony with daily and seasonal rhythms, to understand that food and habits carry as much weight as any single ingredient, and to respect the deep, unhurried relationship between nature and the human body.
            </p>
            <p>
              Botanicals — roots, leaves, bark, berries and naturally occurring minerals — are one part of a broader lifestyle. Consistency matters more than quick fixes. Small, considered choices, repeated across seasons and years, become well-being.
            </p>
          </div>
          <div className="mt-10 flex items-center justify-center">
            <div className="hk-divider w-full max-w-xs"><LeafGlyph className="w-4 h-4" /></div>
          </div>
        </div>
      </section>

      {/* ================= SECTION 3 — Ayurveda in modern life ================= */}
      <section className="relative py-24 lg:py-28 overflow-hidden" data-testid="section-modern-routines">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="max-w-3xl">
            <div className="label-eyebrow">Ayurveda in Modern Life</div>
            <h2 className="serif mt-4 text-[36px] sm:text-[44px] lg:text-[50px] leading-[1.05] text-[var(--hk-ink)]">
              Ancient principles.{" "}
              <span className="italic-accent text-[var(--hk-orange-deep)]">Modern routines.</span>
            </h2>
            <p className="mt-5 text-[15px] leading-[1.85] text-[var(--hk-ink-2)] max-w-2xl">
              Tradition, translated for the pace and shape of a modern life. Four quiet pillars we return to, again and again.
            </p>
          </div>

          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                id: "daily-balance",
                iconVariant: "vine",
                title: "Daily Balance",
                body:
                  "Sleep, movement, mindful choices and steady routines are the true foundation of everyday well-being — quieter than any single ingredient, and far more powerful when practised consistently.",
              },
              {
                id: "thoughtful-nourishment",
                iconVariant: "berry",
                title: "Thoughtful Nourishment",
                body:
                  "What we eat, where it comes from and how we consume it matters. Ayurveda invites us to approach food and natural ingredients with awareness, seasonality, balance and a respect for quality over abundance.",
              },
              {
                id: "botanical-wisdom",
                iconVariant: "root",
                title: "Botanical Wisdom",
                body:
                  "For centuries, India's classical traditions have carefully studied roots, leaves, fruits, bark, seeds and naturally occurring minerals — each considered for its role in the whole, not for isolated miracle claims.",
              },
              {
                id: "long-term-wellbeing",
                iconVariant: "tulsi",
                title: "Long-Term Well-Being",
                body:
                  "True wellness is an ongoing lifestyle, not an occasional reaction to discomfort. The most enduring benefits come from small, considered rituals — practiced patiently, across seasons and years.",
              },
            ].map((c, i) => (
              <article key={c.id} data-testid={`pillar-${c.id}`} className="hk-card p-8 hk-fade-up" style={{ animationDelay: `${i * 80}ms` }}>
                <div className="w-14 h-14 rounded-full bg-[var(--hk-warm-ivory)] border border-[var(--hk-border)] flex items-center justify-center text-[var(--hk-orange-deep)]">
                  <div className="w-8 h-8"><BotanicalIcon variant={c.iconVariant} /></div>
                </div>
                <h3 className="serif mt-6 text-[22px] leading-snug text-[var(--hk-ink)]">{c.title}</h3>
                <p className="mt-3 text-[13.5px] leading-[1.85] text-[var(--hk-ink-2)]">{c.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ================= SECTION 4 — Where Ayurveda Meets HerbKranti ================= */}
      <section className="relative py-24 lg:py-32 bg-[var(--hk-ink)] text-[#EDE4CE] overflow-hidden" data-testid="section-meets-hk">
        {/* Botanical glow */}
        <div className="absolute -top-40 -left-40 w-[520px] h-[520px] rounded-full blur-[120px] opacity-30" style={{ background: "radial-gradient(closest-side, rgba(217,117,53,0.6), transparent 70%)" }} aria-hidden="true" />
        <div className="absolute -bottom-40 -right-40 w-[520px] h-[520px] rounded-full blur-[120px] opacity-25" style={{ background: "radial-gradient(closest-side, rgba(201,169,97,0.5), transparent 70%)" }} aria-hidden="true" />

        <div className="relative mx-auto max-w-4xl px-6 lg:px-10 text-center">
          <div className="label-eyebrow !text-[var(--hk-gold)]">Where Ayurveda Meets HerbKranti</div>
          <h2 className="serif mt-4 text-[36px] sm:text-[46px] lg:text-[56px] leading-[1.05] text-white">
            A quiet return to{" "}
            <span className="italic-accent text-[var(--hk-orange)]">considered wellness.</span>
          </h2>

          <div className="mt-14 grid sm:grid-cols-2 gap-5 max-w-2xl mx-auto">
            <div className="rounded-[14px] border border-white/15 bg-white/[0.04] p-8">
              <div className="label-eyebrow !text-[var(--hk-gold)]">Tradition</div>
              <div className="serif mt-2 text-[24px] text-white leading-tight">Is our foundation.</div>
            </div>
            <div className="rounded-[14px] border border-white/15 bg-white/[0.04] p-8">
              <div className="label-eyebrow !text-[var(--hk-gold)]">Modern life</div>
              <div className="serif mt-2 text-[24px] text-white leading-tight">Is our context.</div>
            </div>
          </div>

          <p className="mt-12 text-[15.5px] leading-[1.95] text-white/75 max-w-2xl mx-auto">
            HerbKranti respects India's traditional wellness wisdom while presenting it in a premium, practical and modern format. Our promise is to make botanical wellness easier to understand — to develop products patiently, to communicate responsibly, and to present traditional ingredients with the quality and elegance they deserve.
          </p>
          <p className="mt-4 text-[15.5px] leading-[1.95] text-white/75 max-w-2xl mx-auto">
            No exaggerated promises. No shortcuts. Products designed for realistic, everyday routines.
          </p>
        </div>
      </section>

      {/* ================= SECTION 5 — Our Approach ================= */}
      <section className="relative py-24 lg:py-28 overflow-hidden" data-testid="section-approach">
        <BotanicalBackdrop variant="sideLeft" />
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="max-w-3xl">
            <div className="label-eyebrow">The HerbKranti Approach</div>
            <h2 className="serif mt-4 text-[36px] sm:text-[44px] lg:text-[50px] leading-[1.05] text-[var(--hk-ink)]">
              Our approach to{" "}
              <span className="italic-accent text-[var(--hk-orange-deep)]">modern wellness.</span>
            </h2>
          </div>

          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-5 gap-5">
            {[
              {
                id: "respect-tradition",
                title: "Respect for Traditional Knowledge",
                body: "We look to India's classical wellness traditions with humility. Ayurveda is a living library, not folklore — and we treat it that way in every conversation and every product.",
              },
              {
                id: "thoughtful-selection",
                title: "Thoughtful Ingredient Selection",
                body: "Every botanical we work with is individually researched and considered for its role in the whole. No filler ingredients, no proprietary-blend obfuscation.",
              },
              {
                id: "quality-first",
                title: "Quality Before Quantity",
                body: "We would rather launch fewer products, more carefully, than expand at the pace the market expects. Every release is a considered addition to the collection.",
              },
              {
                id: "honest-communication",
                title: "Honest and Clear Communication",
                body: "We describe our products as they are — no miracle claims, no guaranteed outcomes, no exaggerated marketing language. What we can honestly say, we say.",
              },
              {
                id: "practical-use",
                title: "Practical Everyday Use",
                body: "Our products are designed to sit gracefully in your daily rhythm — beautifully packaged, thoughtfully portioned, and simple enough to become a real, sustained ritual.",
              },
            ].map((p, i) => (
              <article key={p.id} data-testid={`principle-${p.id}`} className="hk-card p-7 flex flex-col hk-fade-up" style={{ animationDelay: `${i * 70}ms` }}>
                <div className="text-[11px] tracking-[0.24em] uppercase text-[var(--hk-orange-deep)]">0{i + 1}</div>
                <h3 className="serif mt-3 text-[19px] leading-snug text-[var(--hk-ink)]">{p.title}</h3>
                <p className="mt-3 text-[13px] leading-[1.8] text-[var(--hk-ink-2)] flex-1">{p.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ================= SECTION 6 — Botanical Presentation ================= */}
      <section className="relative py-24 lg:py-28 bg-[var(--hk-warm-ivory)] overflow-hidden" data-testid="section-botanicals">
        <BotanicalBackdrop variant="corners" />
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="max-w-3xl">
            <div className="label-eyebrow">A World Shaped by Nature</div>
            <h2 className="serif mt-4 text-[36px] sm:text-[46px] lg:text-[52px] leading-[1.05] text-[var(--hk-ink)]">
              The botanicals that inspire{" "}
              <span className="italic-accent text-[var(--hk-orange-deep)]">our craft.</span>
            </h2>
            <p className="mt-5 text-[15px] leading-[1.85] text-[var(--hk-ink-2)] max-w-2xl">
              India's classical wellness traditions draw from an extraordinary botanical library — roots, leaves, bark, berries and naturally occurring minerals. These are some of the ingredients that inspire the way we think about our products.
            </p>
          </div>

          <div className="mt-12 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-7 gap-3" data-testid="botanical-grid">
            {finalBotanicalRow.map((ing, i) => (
              <div
                key={ing.id}
                data-testid={`botanical-${ing.id}`}
                className="group hk-card p-3 aspect-square flex flex-col items-center justify-center text-center relative overflow-hidden hk-fade-up"
                style={{ animationDelay: `${i * 40}ms` }}
              >
                <div
                  className="absolute inset-0"
                  style={{
                    background:
                      `radial-gradient(circle at 30% 25%, ${ing.accent}22, transparent 65%),` +
                      `linear-gradient(160deg, #F5EFE4 0%, #EFE6D4 55%, ${ing.accent}18 100%)`,
                  }}
                  aria-hidden="true"
                />
                <div className="relative z-10 w-14 h-14 md:w-16 md:h-16" style={{ color: ing.accent }}>
                  <BotanicalIcon variant={ing.iconVariant} />
                </div>
                <div className="relative z-10 mt-2 text-[11.5px] font-medium text-[var(--hk-ink)] leading-tight">
                  {ing.name}
                </div>
                <div className="relative z-10 mt-0.5 text-[9.5px] tracking-[0.14em] uppercase text-[var(--hk-ink-3)] leading-tight px-1">
                  {ing.category}
                </div>
              </div>
            ))}
          </div>

          <p className="mt-8 text-[12px] text-[var(--hk-ink-3)] leading-relaxed max-w-2xl">
            Illustrated for editorial presentation. Botanicals shown are examples that inspire HerbKranti's brand direction; individual product formulations are described on their respective product pages.
          </p>
        </div>
      </section>

      {/* ================= SECTION 7 — HerbKranti Philosophy ================= */}
      <section className="relative py-24 lg:py-28 overflow-hidden" data-testid="section-philosophy">
        <div className="mx-auto max-w-6xl px-6 lg:px-10">
          <div className="text-center max-w-3xl mx-auto">
            <div className="label-eyebrow">More Than Products</div>
            <h2 className="serif mt-4 text-[36px] sm:text-[46px] lg:text-[54px] leading-[1.05] text-[var(--hk-ink)]">
              A wellness{" "}
              <span className="italic-accent text-[var(--hk-orange-deep)]">philosophy.</span>
            </h2>
            <p className="mt-5 text-[15px] leading-[1.85] text-[var(--hk-ink-2)] max-w-xl mx-auto">
              Five quiet principles we return to, in the way we build, present and communicate.
            </p>
          </div>

          <div className="mt-14 space-y-6" data-testid="philosophy-list">
            {[
              { id: "nature-before-noise", title: "Nature before noise", body: "We look to nature first — to the botanicals, rhythms and materials that have shaped Indian wellness for centuries. Trends come and go; the natural world endures." },
              { id: "quality-before-quantity", title: "Quality before quantity", body: "We would rather craft one considered product beautifully than launch a dozen carelessly. Every HerbKranti release is patiently developed and presented with care." },
              { id: "education-before-exaggeration", title: "Education before exaggeration", body: "Wellness deserves clarity, not hyperbole. We explain what our products are, where they come from and why we make them — with restraint and honesty." },
              { id: "routine-before-quick-fixes", title: "Routine before quick fixes", body: "The most meaningful benefits come from small, considered rituals practised patiently. HerbKranti products are designed to belong in your everyday rhythm, not to shortcut it." },
              { id: "trust-before-trends", title: "Trust before trends", body: "Trends are easy. Trust is earned — through provenance, transparency and consistency. That is the standard we hold ourselves to." },
            ].map((p, i) => (
              <div
                key={p.id}
                data-testid={`philosophy-${p.id}`}
                className="hk-card p-8 md:p-10 grid md:grid-cols-[220px_1fr] gap-6 md:gap-10 hk-fade-up"
                style={{ animationDelay: `${i * 90}ms` }}
              >
                <div className="text-[11px] tracking-[0.28em] uppercase text-[var(--hk-orange-deep)] md:pt-1">0{i + 1}</div>
                <div>
                  <h3 className="serif text-[24px] md:text-[28px] leading-tight text-[var(--hk-ink)]">{p.title}.</h3>
                  <p className="mt-3 text-[14.5px] leading-[1.85] text-[var(--hk-ink-2)]">{p.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= SECTION 8 — Closing ================= */}
      <section className="relative py-24 lg:py-32 bg-[var(--hk-warm-ivory)] overflow-hidden" data-testid="section-closing">
        <BotanicalBackdrop variant="corners" />
        <div className="mx-auto max-w-4xl px-6 lg:px-10 text-center">
          <img
            src={IMAGES.LOGO}
            alt="HerbKranti — official logo"
            className="mx-auto w-16 h-16 md:w-20 md:h-20 rounded-2xl object-cover ring-1 ring-[var(--hk-border)] bg-white shadow-[0_10px_28px_-10px_rgba(30,35,31,0.28)]"
            data-testid="home-closing-logo"
            draggable="false"
          />
          <div className="label-eyebrow mt-6">The Beginning</div>
          <h2 className="serif mt-3 text-[36px] sm:text-[46px] lg:text-[56px] leading-[1.05] text-[var(--hk-ink)]">
            Discover Ayurveda,{" "}
            <span className="italic-accent text-[var(--hk-orange-deep)]">the HerbKranti way.</span>
          </h2>
          <p className="mt-8 serif text-[20px] italic text-[var(--hk-ink-2)] leading-snug">
            Rooted in tradition.<br />
            Refined for modern life.<br />
            Built with patience and purpose.
          </p>
          <p className="mt-8 text-[15.5px] leading-[1.9] text-[var(--hk-ink-2)] max-w-2xl mx-auto">
            HerbKranti's collection is growing — thoughtfully, one considered product at a time. We invite you to explore our current and upcoming products, read our story, and join a community that values patience, provenance and considered daily rituals.
          </p>

          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <Link to="/products" className="hk-btn-primary" data-testid="home-closing-products-btn">
              Explore Products <ArrowRight size={16} />
            </Link>
            <Link to="/our-story" className="hk-btn-ghost" data-testid="home-closing-story-btn">
              Our Story
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default HomePage;
